import _ from 'lodash';
import getSafe from "../../Components/JsonScreen/Utils/getSafeValues";
import {durationToMs, humanizeDuration} from "../ChartUtils/chartUtils";

export const makePivotData = (data, rows, values, pivotColumns = [], columns, generatedPivotColumns) => {
    if (rows.length && data.length) {
        //initial need to find duration
        const isDuration = columns.filter((r) => r.type === 'duration');
        //and covert to NUMBER,
        isDuration.length && convertDuration(data, isDuration, true);

        let uniqueRows = createUniqueRows(rows, data);
        let createdData = createData(data, uniqueRows, values, pivotColumns, rows);

        // after generating humanize duration type
        isDuration.length && convertDuration(createdData, generatedPivotColumns, false, generatedPivotColumns);
        return createdData;
    }
    return []
};

function createUniqueRows(rows, data) {
    let uniqueRows = [];
    let keys = rows.map((i) => {
        return i.name
    });
    //function to group rows
    let groupFn = (v) => [rows.map(row => {
        return v[row.name]
    })];
    //rows grouped by function
    let groupedRows = _.groupBy(_.sortBy(data, keys), groupFn);
    let groupedRowsWithKeys = [];
    Object.keys(groupedRows).forEach(keys => {
        let group = [];
        keys.split(',').forEach((key, index) => {
            if (rows[index] && !rows[index].hidden) {
                group.push({
                    value: key,
                    key: rows[index].name,
                    details: rows[index],
                    rows: groupedRows[keys]
                })
            }
        });
        groupedRowsWithKeys.push(group)
    });

    let fullArr = [];
    let prevArr = [];
    groupedRowsWithKeys.forEach(keys => {
        let proceed = true;
        keys.forEach((item, i) => {
            if (keys[i].value !== getSafe(() => prevArr[i].value, undefined)) {
                if (proceed) {
                    keys.slice(i).forEach((key, keyIndex) => {
                        fullArr.push({
                            value: key.value,
                            key: key.key,
                            rows: key.rows,
                            deep: keys.slice(i).length - keyIndex
                        });
                        uniqueRows.push({
                            keys: [{value: key.value, key: key.key, details: key.details}],
                            rows: []
                        });
                    });
                    proceed = false;
                }
            }
        });
        prevArr = keys;
    });
    insertGroups(fullArr);
    fullArr.reverse().forEach((item, index) => {
        uniqueRows[index].deep = item.deep;
        uniqueRows[index].rows = item.newRows
    });

    /**
     * uniqueRows format
     * [{keys: [{value: "", key: ""}], rows: [{...row}]}]
     */
    return uniqueRows
}

function insertGroups(arr) {
    let deepLevel;
    let newRows = [];
    let maxDeepRows = [];
    let maxDeep = _.maxBy(arr, 'deep') && _.maxBy(arr, 'deep').deep;
    for (const [i, item] of arr.reverse().entries()) {
        if (!arr[i].newRows) {
            arr[i].newRows = [];
        }
        if (i === 0) {
            deepLevel = item.deep;
        }
        if (deepLevel >= item.deep) {
            newRows.push(...item.rows);
            maxDeepRows.push(...item.rows);
            deepLevel = item.deep;
            arr[i].newRows.push(...item.rows);
        } else if (item.deep === maxDeep) {
            deepLevel = item.deep;
            arr[i].newRows.push(...maxDeepRows);
            maxDeepRows = [];
        } else {
            deepLevel = item.deep;
            arr[i].newRows.push(...newRows);
            newRows = []
        }
    }
}

function createData(data, uniqueRows, values, pivotColumns) {
    let newValues = [];
    let grandTotalRow = {__isGrandTotal: true};
    let maxDeep = _.maxBy(uniqueRows, 'deep') && _.maxBy(uniqueRows, 'deep').deep;
    uniqueRows.forEach((uniqRow, index) => {
        let newRow = createRows(uniqRow, values, pivotColumns);
        if (index === 0) {
            grandTotalRow = {[uniqRow.keys[0].key]: 'Grand Total', ...grandTotalRow};
        }
        if (maxDeep === uniqRow.deep) {
            countGrandTotal(grandTotalRow, newRow, uniqRow.keys, maxDeep);
        }
        newValues.push(newRow)
    });
    // structure of grouped row
    // {...row, drillDownContent: []}
    // newValues.push(grandTotalRow); //push grand total row in data
    return newValues
}

function createRows(uniqRow, values, pivotColumns) {
    let newRow = {};
    newRow.drillDownContent = [];
    let groupedPivotColumns = pivotColumns.length ? groupPivotColumns(pivotColumns, uniqRow.rows) : [];
    for (let key of uniqRow.keys) {
        newRow[key.key] = key.value;
        newRow.drillDownContent = uniqRow.rows;
        for (let [index, value] of values.entries()) {
            let calcType = value.calculationType;
            switch (calcType) {
                case "sum":
                    calcSum(newRow, uniqRow.rows, value.field, groupedPivotColumns, index);
                    break;
                case "count":
                    calcCount(newRow, uniqRow.rows, value.field, groupedPivotColumns, index);
                    break;
                case "avg":
                    calcAvg(newRow, uniqRow.rows, value.field, groupedPivotColumns, index);
                    break;
                default:
                    break;
            }
        }
    }
    return newRow;
}

function countGrandTotal(grandTotalRow, newRow, keys) {
    //count grandTotals for each column
    Object.keys(newRow).forEach(rowKey => {
        let func = v => v.key === rowKey;
        if (keys.some(func) === false) {
            grandTotalRow[rowKey] = (grandTotalRow[rowKey] || 0) + newRow[rowKey]
        }
    });
}


function calcSum(newRow, values, key, pivotColumns, index) {
    if (pivotColumns.length) {
        for (let column of pivotColumns) {
            let sum = _.sumBy(column.values, key);
            newRow[column.key] = sum;
            newRow.grandTotal = Math.round((newRow.grandTotal || 0) + sum);
        }
        return;
    }
    newRow[key + index] = _.sumBy(values, key);
}

function calcCount(newRow, values, key, pivotColumns, index) {
    if (pivotColumns.length) {
        for (let column of pivotColumns) {
            let count = column.values.length;
            newRow[column.key] = count;
            newRow.grandTotal = Math.round((newRow.grandTotal || 0) + count);
        }
        return;
    }
    newRow[key + index] = values.length;
}

function calcAvg(newRow, values, key, pivotColumns, index) {
    if (pivotColumns.length) {
        for (let column of pivotColumns) {
            let avg = _.meanBy(column.values, key);
            newRow[column.key] = avg;
            newRow.grandTotal = Math.round((newRow.grandTotal || 0) + avg);
        }
        return;
    }
    newRow[key + index] = _.meanBy(values, key);
}


function groupPivotColumns(pivotColumns, values) {
    let currentDataKey = pivotColumns[0].name;
    let groupFn = (v) => {
        return v[currentDataKey]
    };
    let grouped = _.groupBy(values, groupFn);
    return Object.keys(grouped).map(key => {
        return {key, values: grouped[key]};
    });
}

function convertDuration(data, columns = [], toMs) {
    let convertedData = [];
    for (let item of data) {
        for (let column of columns) {
            if (item[column.name] && (column.type === 'duration' || column.name === 'grandTotal')) {
                item[column.name] = toMs ? durationToMs(item[column.name]) : humanizeDuration(item[column.name])
            }
        }
    }
    return convertedData
}